import { Component } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidationErrors, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DynamicDialogComponent } from 'src/app/components/shared/dynamic-dialog/dynamic-dialog.component';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { SentryService } from 'src/app/services/sentry.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UserService } from 'src/app/services/user.service';
import { ApolloService, getAiModel_ai_models_by_pk } from 'src/gql-generated/generated';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgIf, NgClass } from '@angular/common';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

@Component({
    selector: 'app-ai-model-edit',
    templateUrl: './ai-model-edit.component.html',
    styleUrl: './ai-model-edit.component.scss',
    imports: [
        MatButton,
        RouterLink,
        MatIcon,
        NgIf,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatError,
        MatSelect,
        MatOption,
        CdkTextareaAutosize,
        NgClass,
        DynamicDialogComponent,
    ],
})
export class AiModelEditComponent {
    /**
     * Current model id. Only available in editing mode
     */
    aiModelId = this.route.snapshot.paramMap.get('aiModelId');

    /**
     * Loaded model. Only available in editing mode
     */
    aiModel?: getAiModel_ai_models_by_pk;

    /**
     * Formgroup tracking required fields of a model
     */
    aiModelFormGroup = new FormGroup({
        model: new FormControl<string>('', Validators.required),
        type: new FormControl<'llm' | 'embedding' | 'reranker' | undefined>(undefined, Validators.required),
        provider: new FormControl<string>('', Validators.required),
        url: new FormControl<string>('', [
            Validators.required,
            // Valid external or local URL
            Validators.pattern('^(https?://)?([\\da-z.-]+)(:[0-9]+)?([/\\w .-]*)*/?$'),
        ]),
        apiKey: new FormControl<string>('', Validators.required),
        huggingFaceId: new FormControl<string>('', [
            Validators.required,
            // Matches: namespace/repo (allows alphanumeric, dots, underscores, and hyphens)
            Validators.pattern('^[a-zA-Z0-9._-]+/[a-zA-Z0-9._-]+$'),
        ]),
        contextLength: new FormControl<number | undefined>(undefined, [Validators.required, Validators.min(0)]),
        additionalConfig: new FormControl<string>('{}', [
            Validators.required,
            // Validate that input is valid JSON
            (control): ValidationErrors | null => {
                try {
                    JSON.parse(control.value);
                    return null;
                } catch {
                    return { invalidJson: true };
                }
            },
        ]),
    });

    /**
     * True when awaiting an async operation. Used to prevent the user from (accidentally) running a function twice.
     */
    awaitingAsyncOperation = false;

    constructor(
        public breakpoint: BreakpointService,
        private apollo: ApolloService,
        private route: ActivatedRoute,
        private user: UserService,
        private snackbar: SnackbarService,
        private router: Router,
        private sentry: SentryService,
    ) {
        this.loadAiModel();
    }

    /**
     * Load model data
     */
    loadAiModel(): void {
        // Load ai_model if id is found in URL
        if (this.aiModelId) {
            firstValueFrom(this.apollo.getAiModel({ id: this.aiModelId })).then((data) => {
                if (data.data?.ai_models_by_pk) {
                    // Store aiModel and load values into aiModelFormGroup
                    this.aiModel = data.data.ai_models_by_pk;
                    this.aiModelFormGroup.patchValue({
                        model: this.aiModel.model,
                        type: this.aiModel.type as 'llm' | 'embedding' | 'reranker',
                        provider: this.aiModel.provider,
                        url: this.aiModel.url,
                        apiKey: this.aiModel.api_key,
                        huggingFaceId: this.aiModel.huggingface_id,
                        contextLength: this.aiModel.context_length,
                        additionalConfig: JSON.stringify(this.aiModel.additional_config),
                    });
                }
            });
        } else {
            this.aiModelFormGroup.reset();
        }
    }

    /**
     * Attempt to insert the ai model
     */
    async saveAiModel(): Promise<void> {
        if (!this.awaitingAsyncOperation) {
            this.aiModelFormGroup.markAllAsTouched();
            if (this.aiModelFormGroup.valid) {
                this.awaitingAsyncOperation = true;
                try {
                    // Attempt aiModel insert
                    const insertResponse = await firstValueFrom(this.apollo.insertAiModel({
                        ai_model: {
                            model: this.aiModelFormGroup.controls.model.value,
                            provider: this.aiModelFormGroup.controls.provider.value,
                            type: this.aiModelFormGroup.controls.type.value,
                            url: this.aiModelFormGroup.controls.url.value,
                            api_key: this.aiModelFormGroup.controls.apiKey.value,
                            huggingface_id: this.aiModelFormGroup.controls.huggingFaceId.value,
                            context_length: this.aiModelFormGroup.controls.contextLength.value,
                            additional_config: JSON.parse(this.aiModelFormGroup.controls.additionalConfig.value ?? '{}'),
                        },
                    }));
                    // Redirect to ai-models after insert
                    if (insertResponse.data?.insert_ai_models_one?.id) {
                        this.snackbar.success($localize`Model succesvol toegevoegd`);
                        this.router.navigate(['settings', 'models']);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.snackbar.error($localize`Model toevoegen mislukt`);
                }
            }
            this.awaitingAsyncOperation = false;
        }
    }

    /**
     * Attempt to save ai model updates
     * @param dialog confirm dialog that appears if the model is in use by at least 1 organization
     */
    async updateAiModel(dialog: DynamicDialogComponent): Promise<void> {
        // Let user confirm update if the model is in use by > 0 organizations
        if (this.aiModel?.model_usage_count) {
            const accepted = await dialog.open();
            if (!accepted) {
                return;
            }
        }

        if (!this.awaitingAsyncOperation) {
            this.aiModelFormGroup.markAllAsTouched();
            if (this.aiModelFormGroup.valid && this.aiModel) {
                this.awaitingAsyncOperation = true;
                try {
                    // Attempt aiModel update
                    const updateReponse = await firstValueFrom(this.apollo.updateAiModel({
                        id: this.aiModel.id,
                        aiModelUpdate: {
                            model: this.aiModelFormGroup.controls.model.value,
                            provider: this.aiModelFormGroup.controls.provider.value,
                            type: this.aiModelFormGroup.controls.type.value,
                            url: this.aiModelFormGroup.controls.url.value,
                            api_key: this.aiModelFormGroup.controls.apiKey.value,
                            huggingface_id: this.aiModelFormGroup.controls.huggingFaceId.value,
                            context_length: this.aiModelFormGroup.controls.contextLength.value,
                            additional_config: JSON.parse(this.aiModelFormGroup.controls.additionalConfig.value ?? '{}'),
                            updated_by_id: this.user.profile.id,
                        },
                    }));
                    // If update is successful, redirect the user to models page.
                    if (updateReponse.data?.update_ai_models_by_pk?.id) {
                        this.snackbar.success($localize`Model succesvol aangepast`);
                        this.router.navigate(['settings', 'models']);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.snackbar.error($localize`Model aanpassen mislukt`);
                }
            }
            this.awaitingAsyncOperation = false;
        }
    }

    /**
     * Attempts to soft-delete the ai model
     * @param dialog Dialog to ask for confirmation
     */
    async deleteAiModel(dialog: DynamicDialogComponent): Promise<void> {
        // Can't delete models that are in use by organizations
        if (this.aiModel?.model_usage_count) {
            return;
        }

        if (this.aiModel) {
            // Await confirmation
            const accepted = await dialog.open();
            if (accepted) {
                try {
                    // Soft-delete ai model in hasura
                    const updateResponse = await firstValueFrom(this.apollo.updateAiModel({
                        id: this.aiModel.id,
                        aiModelUpdate: {
                            deleted_at: 'now()',
                            deleted_by_id: this.user.profile.id,
                        },
                    }));

                    // If update is successful, notify end-user and navigate to models
                    if (updateResponse.data?.update_ai_models_by_pk?.id) {
                        this.snackbar.success($localize`Model succesvol verwijderd`);
                        // Refresh data
                        this.router.navigate(['settings', 'models']);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.snackbar.error($localize`Model verwijderen mislukt`);
                }
            }
        }
    }
}
